'use client';

import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import {
  FilmSlate, MusicNote, SpeakerHigh, WaveTriangle, CameraPlus, Images,
  GlobeHemisphereWest, MagnifyingGlass, Sparkle,
} from '@phosphor-icons/react';
import { Shimmer } from './shimmer';
import { cn } from '@/lib/utils';
import { getToolDisplay, getToolStateTitle, getToolTheme } from '@/lib/tool-display';
import { formatLiveArgsJson } from '@/lib/streaming-tool-input';

/**
 * Creative, themed tool loaders. Each uses motion/react for robust animation —
 * no styled-jsx keyframes that sometimes don't load. Everything dances.
 */

// =============================================================================
// Shared frame — has an ANIMATED GRADIENT WASH behind the content
// =============================================================================

type ThemeKey = 'violet' | 'cyan' | 'amber' | 'pink' | 'emerald' | 'sky' | 'indigo';

const THEME: Record<ThemeKey, { border: string; tint: string; gradient: string[]; chip: string; accent: string; text: string; soft: string }> = {
  violet:  { border: 'border-violet-500/25',  tint: 'bg-violet-500/[0.05]',  gradient: ['#8b5cf6', '#a855f7', '#ec4899'], chip: 'border-violet-500/30 bg-violet-500/15 text-violet-300',  accent: 'text-violet-300',  text: 'text-violet-50',  soft: 'text-violet-100/70' },
  cyan:    { border: 'border-cyan-500/25',    tint: 'bg-cyan-500/[0.05]',    gradient: ['#22d3ee', '#06b6d4', '#0891b2'], chip: 'border-cyan-500/30 bg-cyan-500/15 text-cyan-300',      accent: 'text-cyan-300',    text: 'text-cyan-50',    soft: 'text-cyan-100/70' },
  amber:   { border: 'border-amber-500/25',   tint: 'bg-amber-500/[0.05]',   gradient: ['#fbbf24', '#f59e0b', '#f97316'], chip: 'border-amber-500/30 bg-amber-500/15 text-amber-300',   accent: 'text-amber-300',   text: 'text-amber-50',   soft: 'text-amber-100/70' },
  pink:    { border: 'border-pink-500/25',    tint: 'bg-pink-500/[0.05]',    gradient: ['#f472b6', '#ec4899', '#db2777'], chip: 'border-pink-500/30 bg-pink-500/15 text-pink-300',      accent: 'text-pink-300',    text: 'text-pink-50',    soft: 'text-pink-100/70' },
  emerald: { border: 'border-emerald-500/25', tint: 'bg-emerald-500/[0.05]', gradient: ['#34d399', '#10b981', '#059669'], chip: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300', accent: 'text-emerald-300', text: 'text-emerald-50', soft: 'text-emerald-100/70' },
  sky:     { border: 'border-sky-500/25',     tint: 'bg-sky-500/[0.05]',     gradient: ['#38bdf8', '#0ea5e9', '#0284c7'], chip: 'border-sky-500/30 bg-sky-500/15 text-sky-300',          accent: 'text-sky-300',     text: 'text-sky-50',     soft: 'text-sky-100/70' },
  indigo:  { border: 'border-indigo-500/25',  tint: 'bg-indigo-500/[0.05]',  gradient: ['#818cf8', '#6366f1', '#4f46e5'], chip: 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300', accent: 'text-indigo-300', text: 'text-indigo-50',  soft: 'text-indigo-100/70' },
};

function LoaderFrame({
  theme,
  hero,
  title,
  subtitle,
  meta,
}: {
  theme: ThemeKey;
  hero: ReactNode;
  title: string;
  subtitle?: ReactNode;
  meta?: ReactNode;
}) {
  const t = THEME[theme];
  const [a, b, c] = t.gradient;
  return (
    <div className={cn('relative overflow-hidden rounded-xl border px-4 py-3 backdrop-blur-sm', t.border, t.tint)}>
      {/* Animated aurora wash behind content */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background: `conic-gradient(from 0deg at 50% 50%, ${a}40, ${b}40, ${c}40, ${a}40)`,
          filter: 'blur(24px)',
        }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 14, ease: 'linear' }}
      />
      {/* Sheen sweep */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(110deg, transparent 35%, ${b}20 50%, transparent 65%)`,
          backgroundSize: '250% 100%',
        }}
        animate={{ backgroundPosition: ['250% 0', '-150% 0'] }}
        transition={{ repeat: Infinity, duration: 3.2, ease: 'linear' }}
      />

      <div className="relative flex items-start gap-3">
        <div className={cn('relative mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-xl border', t.chip)}>
          {hero}
        </div>
        <div className="min-w-0 flex-1">
          <Shimmer as="p" className={cn('text-sm font-medium', t.text)} duration={1.6}>
            {title}
          </Shimmer>
          {subtitle ? <p className={cn('mt-1 text-xs', t.soft)}>{subtitle}</p> : null}
          {meta ? <div className="mt-2">{meta}</div> : null}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// VIDEO COMPOSER — 4-phase dot choreography via motion variants
// =============================================================================

const VIDEO_PALETTE = ['#8b5cf6', '#a855f7', '#ec4899', '#06b6d4', '#f59e0b'];

export function VideoComposerLoader({
  title = 'Composing video',
  subtitle,
  streamingCode,
}: {
  title?: string;
  subtitle?: ReactNode;
  /** When the model is still streaming the Remotion componentCode, pass it here
   *  so we render a low-opacity live preview — the user can see chars arriving
   *  and instantly spot if the stream has frozen. */
  streamingCode?: string;
}) {
  const cols = 7;
  const rows = 4;
  const playCells = new Set(['2,1', '2,2', '3,1', '3,2', '4,2']); // play triangle
  const progressCells = new Set(['0,2', '1,2', '2,2', '3,2', '4,2', '5,2', '6,2']); // center row

  const dots = [];
  const cycleDuration = 3.8;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const key = `${c},${r}`;
      const isPlay = playCells.has(key);
      const isProgress = progressCells.has(key);
      const color = VIDEO_PALETTE[(c + r) % VIDEO_PALETTE.length];
      const formationDelay = ((c + (rows - 1 - r)) / (cols + rows - 2)) * 0.6;
      const progressOrder = c * 0.07;
      const dx = c - (cols - 1) / 2;

      // Keyframes for each role. Arrays are sampled evenly across the cycle.
      const opacityFrames = isPlay
        ? [0, 1, 1, 1, 0.7, 0]
        : isProgress
          ? [0, 1, 0.6, 1, 0.9, 0]
          : [0, 1, 0.35, 0.35, 0.7, 0];
      const scaleFrames = isPlay
        ? [0.3, 1, 1.8, 1.4, 0.9, 0]
        : isProgress
          ? [0.3, 1, 0.7, 1.4, 1.1, 0]
          : [0.3, 1, 0.7, 0.7, 1.1, 0];
      const xFrames = isPlay
        ? [0, 0, 0, 0, dx * 4, dx * 14]
        : [0, 0, 0, 0, dx * 3, dx * 12];

      dots.push(
        <motion.span
          key={key}
          className="block rounded-full"
          style={{ width: 6, height: 6, backgroundColor: color, boxShadow: `0 0 8px ${color}88` }}
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{ opacity: opacityFrames, scale: scaleFrames, x: xFrames }}
          transition={{
            duration: cycleDuration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: isProgress ? progressOrder : formationDelay,
            times: [0, 0.2, 0.45, 0.65, 0.85, 1],
          }}
        />
      );
    }
  }

  const hasCode = typeof streamingCode === 'string' && streamingCode.trim().length > 0;
  // Show only the last N lines of code so the preview stays small and users see the latest streaming activity
  const tailLines = hasCode
    ? streamingCode!
        .split('\n')
        .slice(-10)
        .join('\n')
    : '';

  return (
    <LoaderFrame
      theme="violet"
      hero={<FilmSlate weight="duotone" className="size-5 text-violet-200" />}
      title={title}
      subtitle={subtitle}
      meta={
        <div className="space-y-2">
          <motion.div
            className="inline-grid p-2 rounded-lg"
            style={{
              gridTemplateColumns: `repeat(${cols}, 6px)`,
              gridTemplateRows: `repeat(${rows}, 6px)`,
              gap: 6,
              background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(236,72,153,0.08))',
              border: '1px solid rgba(139,92,246,0.25)',
            }}
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: cycleDuration, repeat: Infinity, ease: 'easeInOut' }}
          >
            {dots}
          </motion.div>

          {hasCode ? (
            <div className="relative rounded-md border border-violet-500/20 bg-violet-950/30 max-h-28 overflow-hidden">
              {/* Code at 0.4 opacity — visible but clearly a "not final" preview */}
              <pre className="px-2 py-1.5 text-[10px] leading-snug font-mono text-violet-50/40 whitespace-pre overflow-hidden">
                {tailLines}
              </pre>
              {/* Fade-to-transparent mask at the top so tail-of-stream feels like it's flowing up */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-violet-950/40 to-transparent" />
              {/* Char counter so even if text isn't visible the user sees something tick */}
              <div className="absolute right-1.5 bottom-1 text-[9px] font-mono text-violet-300/60 bg-violet-950/60 px-1.5 py-0.5 rounded">
                {streamingCode!.length} chars
              </div>
            </div>
          ) : null}
        </div>
      }
    />
  );
}

// =============================================================================
// SPEECH — 18 vertical bars doing a traveling waveform
// =============================================================================

export function SpeechLoader({ title = 'Generating voiceover', subtitle }: { title?: string; subtitle?: ReactNode }) {
  const barCount = 22;
  return (
    <LoaderFrame
      theme="cyan"
      hero={<SpeakerHigh weight="duotone" className="size-5 text-cyan-200" />}
      title={title}
      subtitle={subtitle}
      meta={
        <div className="inline-flex items-center gap-[3px] h-7 px-3 rounded-full bg-cyan-500/10 border border-cyan-500/25">
          {Array.from({ length: barCount }, (_, i) => {
            // Phase per bar creates a traveling wave
            const phase = (i / barCount) * Math.PI * 2;
            // Keyframes shaped like |sin(phase + t)|
            const frames = [0.3, 0.8, 1, 0.5, 0.9, 0.2, 0.3];
            return (
              <motion.span
                key={i}
                className="w-[3px] rounded-full"
                style={{
                  background: 'linear-gradient(180deg, #67e8f9, #0891b2)',
                  boxShadow: '0 0 4px rgba(34,211,238,0.5)',
                }}
                initial={{ scaleY: 0.3 }}
                animate={{ scaleY: frames }}
                transition={{
                  duration: 1.1,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: phase / (Math.PI * 2) * 0.8,
                }}
              />
            );
          })}
        </div>
      }
    />
  );
}

// =============================================================================
// MUSIC — equalizer bars with staggered jumps and rainbow hue
// =============================================================================

export function MusicLoader({ title = 'Composing music', subtitle }: { title?: string; subtitle?: ReactNode }) {
  const barCount = 12;
  return (
    <LoaderFrame
      theme="amber"
      hero={<MusicNote weight="duotone" className="size-5 text-amber-200" />}
      title={title}
      subtitle={subtitle}
      meta={
        <div className="inline-flex items-end gap-[3px] h-8 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/25">
          {Array.from({ length: barCount }, (_, i) => {
            const hue = 20 + i * 10;
            return (
              <motion.span
                key={i}
                className="w-[3.5px] rounded-sm"
                style={{
                  background: `linear-gradient(180deg, hsl(${hue}, 90%, 65%), hsl(${hue}, 85%, 50%))`,
                  boxShadow: `0 0 5px hsla(${hue}, 90%, 60%, 0.5)`,
                  transformOrigin: 'bottom',
                  height: '100%',
                }}
                initial={{ scaleY: 0.25 }}
                animate={{ scaleY: [0.25, 0.9, 0.4, 1, 0.55, 0.25] }}
                transition={{
                  duration: 0.9 + (i % 3) * 0.15,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: (i % 5) * 0.12,
                }}
              />
            );
          })}
        </div>
      }
    />
  );
}

// =============================================================================
// SFX — radial rings emanating from a glowing core
// =============================================================================

export function SfxLoader({ title = 'Generating sound effects', subtitle }: { title?: string; subtitle?: ReactNode }) {
  return (
    <LoaderFrame
      theme="pink"
      hero={<WaveTriangle weight="duotone" className="size-5 text-pink-200" />}
      title={title}
      subtitle={subtitle}
      meta={
        <div className="relative w-14 h-8 flex items-center justify-center">
          {/* Core */}
          <motion.span
            className="absolute size-2 rounded-full"
            style={{ background: '#f472b6', boxShadow: '0 0 10px rgba(244,114,182,0.9)' }}
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Three expanding rings */}
          {[0, 0.4, 0.8].map((delay, i) => (
            <motion.span
              key={i}
              className="absolute size-3 rounded-full border-2"
              style={{ borderColor: 'rgba(244,114,182,0.7)' }}
              initial={{ scale: 0.4, opacity: 0.9 }}
              animate={{ scale: [0.4, 2.6], opacity: [0.9, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, delay, ease: 'easeOut' }}
            />
          ))}
        </div>
      }
    />
  );
}

// =============================================================================
// IMAGE GEN — photo tiles that fade through colors
// =============================================================================

export function ImageGenLoader({
  title,
  subtitle,
  count,
  model,
}: {
  title?: string;
  subtitle?: ReactNode;
  count?: number;
  model?: string;
}) {
  const tileCount = count && count > 1 ? Math.min(count, 6) : 3;
  // gpt-image-2 runs an O-series reasoning step before drawing — 60–150s
  // typical, vs 10–25s for Nano Banana / Imagen-class models. Surface that
  // up front so users don't think the request stalled.
  const isSlowModel = model === 'gpt-image-2';
  const resolvedTitle =
    title ?? (count && count > 1
      ? `Generating ${count} images`
      : isSlowModel
        ? 'Painting your image'
        : 'Generating image');
  const resolvedSubtitle =
    subtitle ?? (isSlowModel ? 'gpt-image-2 · usually 1–2 min' : undefined);
  return (
    <LoaderFrame
      theme="emerald"
      hero={<CameraPlus weight="duotone" className="size-5 text-emerald-200" />}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      meta={
        <div className="inline-flex gap-1 p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
          {Array.from({ length: tileCount }, (_, i) => (
            <motion.span
              key={i}
              className="block rounded"
              style={{
                width: 22,
                height: 14,
                background: `linear-gradient(135deg, hsl(${140 + i * 20}, 70%, 55%), hsl(${180 + i * 20}, 70%, 45%))`,
              }}
              initial={{ opacity: 0.25, scale: 0.9 }}
              animate={{
                opacity: [0.25, 1, 0.65, 1, 0.25],
                scale: [0.9, 1.05, 0.95, 1.05, 0.9],
              }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                delay: i * 0.18,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      }
    />
  );
}

// =============================================================================
// IMAGE SEARCH — magnifier glint over a 4-tile mosaic
// =============================================================================

export function ImageSearchLoader({ title = 'Searching for images', query }: { title?: string; query?: string }) {
  return (
    <LoaderFrame
      theme="violet"
      hero={
        <div className="relative">
          <Images weight="duotone" className="size-5 text-violet-200" />
          <motion.span
            className="absolute -bottom-1 -right-1 rounded-full bg-background p-[2px]"
            animate={{ rotate: [0, 12, -8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <MagnifyingGlass weight="bold" className="size-3 text-violet-300" />
          </motion.span>
        </div>
      }
      title={title}
      subtitle={query ? <>Looking up <span className="text-violet-50">“{query}”</span></> : 'Looking up images'}
    />
  );
}

// =============================================================================
// WEB SEARCH — globe with orbiting magnifier
// =============================================================================

export function WebSearchLoader({ title = 'Searching the web', query }: { title?: string; query?: string }) {
  return (
    <LoaderFrame
      theme="sky"
      hero={
        <div className="relative">
          {/* Globe breathes instead of rotating — spinning an asymmetric
              icon reads as a stuck spinner. */}
          <motion.span
            animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="block"
          >
            <GlobeHemisphereWest weight="duotone" className="size-5 text-sky-200" />
          </motion.span>
          <motion.span
            className="absolute -bottom-1 -right-1 rounded-full bg-background p-[2px]"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <MagnifyingGlass weight="bold" className="size-3 text-sky-300" />
          </motion.span>
        </div>
      }
      title={title}
      subtitle={query ? <>Looking up <span className="text-sky-50">“{query}”</span></> : 'Looking up your query'}
    />
  );
}

// =============================================================================
// GENERIC FALLBACK — shimmer beam + flowing particles
// =============================================================================

export function GenericToolLoader({
  toolName,
  title,
  subtitle = 'Working on it…',
  HeroIcon,
  input,
}: {
  toolName: string;
  /** Optional override for the loader headline. Defaults to the tool's
   *  registered loadingTitle (or `Running ${toolName}` if unregistered). */
  title?: string;
  subtitle?: string;
  /** Optional override for the rotating hero icon. Defaults to the tool's
   *  registered icon (Sparkle if unregistered). */
  HeroIcon?: typeof Sparkle;
  /** Partial streaming input. When present it's shown as a compact, capped
   *  live-args JSON preview so even generic tools feel alive as their call
   *  is generated — not a bare "Pending" spinner. */
  input?: unknown;
}) {
  const display = getToolDisplay(toolName);
  const Hero = HeroIcon ?? display.icon;
  const argsPreview = formatLiveArgsJson(input);
  return (
    <LoaderFrame
      theme="indigo"
      hero={
        <motion.span
          animate={{ opacity: [0.55, 1, 0.7], scale: [0.96, 1.04, 0.98] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Hero weight="duotone" className="size-5 text-indigo-200" />
        </motion.span>
      }
      title={title ?? display.loadingTitle}
      subtitle={subtitle}
      meta={
        <div className="space-y-2">
          <div className="relative w-16 h-1 rounded-full bg-indigo-500/20 overflow-hidden">
            <motion.span
              className="absolute top-0 bottom-0 w-3 rounded-full"
              style={{ background: 'linear-gradient(90deg, transparent, #a5b4fc, transparent)' }}
              animate={{ x: [-12, 64] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
            />
          </div>
          {argsPreview ? (
            <pre className="max-h-32 overflow-auto rounded-md border border-indigo-500/20 bg-indigo-950/30 px-2 py-1.5 font-mono text-[10px] leading-snug text-indigo-50/70 whitespace-pre-wrap break-words">
              {argsPreview}
            </pre>
          ) : null}
        </div>
      }
    />
  );
}

function capabilityTheme(toolName: string): ThemeKey {
  return getToolTheme(toolName);
}

function browserSubtitle(toolName: string) {
  switch (toolName) {
    case 'createBrowser':
      return 'Starting a visible browser session in the side workspace';
    case 'browserLiveView':
      return 'Opening the live browser view where you can watch the page';
    case 'browserNavigate':
      return 'Opening the requested website in a visible browser';
    case 'browserScroll':
      return 'Scrolling the visible browser page';
    case 'browserPlaywright':
      return 'Navigating, clicking, reading, or capturing page state';
    case 'browserScreenshot':
      return 'Saving a screenshot from the active browser page';
    case 'connectBrowser':
      return 'Restoring an existing browser session';
    case 'listBrowsers':
      return 'Looking for active browser sessions to reuse';
    default:
      return 'Working inside the active browser session';
  }
}

function sandboxSubtitle(toolName: string) {
  switch (toolName) {
    case 'createSandbox':
      return 'Starting an isolated workspace with files and runtime state';
    case 'connectSandbox':
      return 'Reconnecting to a persisted sandbox filesystem';
    case 'sandboxExec':
      return 'Executing a shell command inside the sandbox';
    case 'sandboxCodeRun':
      return 'Running code in the sandbox runtime';
    case 'sandboxSessionCreate':
      return 'Opening a persistent terminal session in the sandbox';
    case 'sandboxSessionList':
      return 'Checking persistent terminal sessions';
    case 'sandboxSessionExec':
      return 'Running a command in a persistent terminal session';
    case 'sandboxSessionLogs':
      return 'Reading persistent terminal command logs';
    case 'sandboxSessionDelete':
      return 'Closing a persistent terminal session';
    case 'sandboxPreviewUrl':
      return 'Opening a dev-server preview from the sandbox';
    case 'sandboxReadFile':
    case 'sandboxWriteFile':
    case 'sandboxListFiles':
    case 'sandboxSearchFiles':
      return 'Working with files in the sandbox filesystem';
    default:
      return 'Using the isolated code workspace';
  }
}

function CompactActionStatus({
  toolName,
  subtitle,
}: {
  toolName: string;
  subtitle?: string;
}) {
  const display = getToolDisplay(toolName);
  const Hero = display.icon;
  const theme = capabilityTheme(toolName);
  const t = THEME[theme];
  const title = getToolStateTitle(toolName, 'running');

  return (
    <div
      className="my-2 inline-flex max-w-full items-center gap-2.5 rounded-md px-1.5 py-1"
      aria-label={subtitle ? `${title}. ${subtitle}` : title}
    >
      <span className={cn('grid size-7 shrink-0 place-items-center rounded-md border', t.chip)}>
        <motion.span
          animate={{ opacity: [0.58, 1, 0.72], scale: [0.96, 1.04, 0.98] }}
          transition={{ duration: 1.35, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Hero weight="duotone" className="size-4" />
        </motion.span>
      </span>
      <Shimmer
        as="span"
        className={cn('min-w-0 truncate text-sm font-medium', t.text)}
        duration={1.35}
      >
        {title}
      </Shimmer>
    </div>
  );
}

export function CapabilityActionLoader({
  toolName,
  subtitle,
}: {
  toolName: string;
  subtitle?: string;
}) {
  const theme = capabilityTheme(toolName);
  const resolvedSubtitle = subtitle
    ?? (theme === 'sky'
      ? browserSubtitle(toolName)
      : theme === 'emerald'
        ? sandboxSubtitle(toolName)
        : toolName === 'showWorkspace'
          ? 'Reopening the latest visual or live view in the side workspace'
          : toolName === 'listCapabilities'
            ? 'Reading the available skills and tool actions'
            : toolName === 'callTool' || toolName === 'callCapability'
              ? 'Resolving tool details'
            : 'Loading tool details');

  return <CompactActionStatus toolName={toolName} subtitle={resolvedSubtitle} />;
}
